import {CurrencyPipe} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
  EmTranslatePipe,
  FeatureFlagService,
  ToastService,
  TranslationService,
} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  Duration,
  durationToNumber,
  HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
  HUMANIA_EMMA_V2_MAX_COVERAGE,
  HUMANIA_EMMA_V2_MAX_TERM_AGE,
  HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
  HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS,
  ProviderId,
} from '@emma-labs/shared/typescript';
import {Button} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {DialogService} from 'primeng/dynamicdialog';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TableModule} from 'primeng/table';
import {finalize} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProtectionService} from '../../../../../services/protection.service';
import {Product} from '../../coverage-builder-section.component';
import {CoverageCardComponent} from '../../coverage-card/coverage-card.component';
import {BaseLifeInsuranceCardComponent} from '../base-life-insurance-card.component';
import {RenewalPricesComponent} from './renewal-prices/renewal-prices.component';

const DEFAULT_SELECTED_DURATION = Duration.T10;
const DEFAULT_SHOWN_DURATIONS = [DEFAULT_SELECTED_DURATION, Duration.T20, Duration.T25];

@Component({
  selector: 'em-term-life-card',
  templateUrl: './term-life-card.component.html',
  imports: [
    CardModule,
    CoverageCardComponent,
    DropdownModule,
    ReactiveFormsModule,
    TableModule,
    Button,
    CurrencyPipe,
    ProgressSpinnerModule,
    EmTranslatePipe,
  ],
})
export class TermLifeCardComponent extends BaseLifeInsuranceCardComponent {
  @Input({required: true}) override set selectedTermLifeCoverageAmount(amount: number | undefined) {
    this.form.controls.coverageAmount.setValue(amount, {emitEvent: false});
    if (!amount) {
      this.form.controls.duration.setValue(DEFAULT_SELECTED_DURATION, {emitEvent: false});
    }
    this.setProducts();
  }

  @Input({required: true}) override set selectedWholeLifeCoverageAmount(
    wholeLifeCoverageAmount: number | undefined,
  ) {
    this.hasDuo = !!wholeLifeCoverageAmount;

    this.coverageAmountOptions = this.protectionService.getCoverageDropdownOptions(
      HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
      HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
      HUMANIA_EMMA_V2_MAX_COVERAGE - (wholeLifeCoverageAmount ?? 0),
      this.hasDuo,
    );

    this.setProducts();
  }

  mainProducts: Product[] = [];
  allPossibleProducts: Product[] = [];
  showAllProducts = false;

  constructor(
    override readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly translate: TranslationService,
    private readonly protectionService: ProtectionService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly dialogService: DialogService,
  ) {
    super(fb, DEFAULT_SELECTED_DURATION);
  }

  toggleShowAllDurations(): void {
    this.showAllProducts = !this.showAllProducts;
  }

  openRenewalPricesDialog(): void {
    const coverages = this.allPossibleProducts.map((product) => {
      return {lifeInsurances: {[product.value]: this.form.controls.coverageAmount.value}};
    });

    this.dialogService.open(RenewalPricesComponent, {
      closable: true,
      closeOnEscape: true,
      width: '80%',
      data: {
        ratesVersion: this._ratesVersion,
        coverages,
        providerId: ProviderId.HUMANIA_EMMA_V2,
        categoryCodes: [CategoryCode.C1],
        age: this._age,
        smoker: this._smoker,
        sex: this._sex,
        termCoverageDuration: this.form.controls.duration.value,
        defaultShownDurations: DEFAULT_SHOWN_DURATIONS,
        hasWholeLife: this.hasDuo,
      },
    });
  }

  setProducts(): void {
    if (!this.withProductSelection) {
      return;
    }

    if (
      !this._ratesVersion ||
      this._smoker == null ||
      this._age == null ||
      this._age > HUMANIA_EMMA_V2_MAX_TERM_AGE ||
      this.hasDuo == null ||
      !this._sex
    ) {
      this.coverageAmountOptions = [];
      return;
    }

    if (!this.form.controls.coverageAmount.value || !this.form.controls.duration.value) {
      return;
    }

    const coverages = HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS.map((duration) => {
      return {lifeInsurances: {[duration]: this.form.controls.coverageAmount.value}};
    });

    this.protectionService
      .getProtections(
        this._ratesVersion!,
        coverages,
        ProviderId.HUMANIA_EMMA_V2,
        [CategoryCode.C1],
        this._age!,
        this._smoker!,
        this._sex!,
        {allowPartialSuccess: true},
      )
      .pipe(
        map((protections) => {
          return protections.map((p, index) => {
            const pi = p.personInsurances[0];
            if (!pi) {
              throw new Error('Person insurance not found');
            }

            if (pi.duration === this.form.controls.duration.value) {
              this.selectedProductIndex = index;
            }

            const durationYear = durationToNumber(pi.duration);
            return {
              name: {
                en: `${durationYear}-year term`,
                fr: `Temporaire de ${durationYear} ans`,
              },
              value: pi.duration,
              price: this.hasDuo
                ? pi.priceBreakdown.basePremium
                : pi.priceBreakdown.basePremium + pi.priceBreakdown.policyFee,
            };
          });
        }),
        finalize(() => (this.isPriceLoading = false)),
      )
      .subscribe({
        next: (allPossibleProducts) => {
          this.allPossibleProducts = allPossibleProducts;

          this.mainProducts = this.allPossibleProducts.filter((product) => {
            return (
              DEFAULT_SHOWN_DURATIONS.includes(product.value as Duration) ||
              this.allPossibleProducts[this.selectedProductIndex] === product
            );
          });
        },
        error: (_) => {
          this.toastService.error(
            this.translate.currentLanguageIsFr()
              ? `Une erreur est survenue lors du chargement de l'assurance temporaire. Veuillez réessayer`
              : `An error occurred while loading the term insurance. Please try again`,
          );
        },
      });
  }
}
